import { useEffect, useRef, useState } from 'react';
import { CampaignManagementQuery } from '../../services/query';
import { CampaignManagementCommand } from '../../services/command';
import {
    FilterFields,
    IAsset,
    IAssetsFolder,
    IBadge,
    IExpandObjects,
    IProperties,
    ISearchResult,
    NestedFolderProps,
    BadgeButtonProps,
    SelectedBadgeButtonProps,
    ICampaignDictionaryItem,
    AssigneeOptionType,
} from './types/interfaces/CampaingTypes';
import { enumSnackbarStatusType } from '../../types/enums';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    CampaignRepeatType,
    CampaignStatus,
    CampaignStatuses,
    CampaignStep,
    CampaignSteps,
    CouponCharacterSetTypes,
    RuleCreditCardTypeValues,
    RuleCustomerTypeValues,
    RuleDeliveryMethodValues,
    RulePaymentCollectorValues,
    RulePaymentMethodValues,
    RulePlatformValues,
    RuleShippingCompanyValues,
    WebsiteFolderType,
} from '../../types/enums/campaign-enums';
import Helper from '../../utils/helper';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../stores/redux/actions';
import { StoreActionType } from '../../stores/redux/types';

const getQueryParams = (props: any) => {
    const urlParams = props && props.match! && new URLSearchParams(props.match.params);
    return {
        id: urlParams! && urlParams.get('id'),
    };
};

export const CampaignServiceComponent = (props: any) => {
    const dispatch = useDispatch();

    const history = useHistory();

    const { id } = getQueryParams(props);
    const { t } = useTranslation();

    const regionStore = useSelector((state: any) => state.region);
    const countryId: any = useSelector((state: any) => state.lastRegionReducer.setLastRegion);
    let loadingProgressCount = 0;
    const [loading, setLoading] = useState<boolean>(false);

    const [errors, setErrors] = useState({});

    const [step, setStep] = useState<number>(CampaignStep.Properties);
    const [liveCampaignStatus, setLiveCampaignStatus] = useState<number>(CampaignStatus.Active);

    const [campaignDetailInitial, setCampaignDetailInitial] = useState<any>({});

    const [campaignDetail, setCampaignDetail] = useState<any>({});

    const [properties, setProperties] = useState<IProperties>({
        assignee: '',
        beginDate: '',
        campaignId: 0,
        campaignName: '',
        region: Number(regionStore?.region),
        endDate: '',
        endTime: '08:00',
        priority: undefined,
        reiteration: 0,
        recurrences: [],
        startTime: '08:00',
        tag: '',
        status: 0,
    });

    const [assignees, setAssignees] = useState<AssigneeOptionType[]>([]);

    const initialUpdatedCachedVersion = {
        campaignRuleId: id != undefined && id > 0 ? id : 0,
        reloadAllCampaignRuleMatrix: false,
        reloadAllCampaignRulePriority: false,
        reloadCampaignRuleCoupons: false,
        reloadCampaignRuleProducts: false,
        reloadCampaignRuleEmails: false,
        keywords: [] as string[],
    };

    const [updatedCachedVersion, setUpdatedCacheVersion] = useState(initialUpdatedCachedVersion);

    const [expanded, setExpanded] = useState<IExpandObjects>({
        campaingMainProperties: true,
        couponUsage: true,
        thirdPartyCampaigns: true,
        publicationInformation: true,
        freeCargo: true,
        campaignDuration: true,
        backOfficeUser: true,
    });

    const initialState: FilterFields = {
        campaignFilters: {
            region: { id: Number(regionStore?.region), name: '' },
            campaignName: null,
            applicationScopeType: null,
            beginDate: null,
            endDate: null,
            startTime: { id: '08:00', value: '08:00' },
            endTime: { id: '08:00', value: '08:00' },
            assignee: null,
            processOrder: null,
            platforms: null,
            tag: null,
            stackable: null,
            status: {
                id: CampaignStatus.Active,
                name: CampaignStatuses.find((x: any) => x.id == CampaignStatus.Active)?.name,
            },
        },
    };

    const getRuleCustomerTypeValuesByRegion = () => {
        const list = RuleCustomerTypeValues.filter((x: any) =>
            x.regions.includes(Number(regionStore?.region))
        ).map<any>((x: any) => ({
            ...x,
            name: t(x.name),
        }));
        const sortedList = list.sort((a: any, b: any) => a.name.localeCompare(b.name));
        return sortedList;
    };

    const getRuleDeliveryMethodValuesByRegion = () => {
        const list = RuleDeliveryMethodValues.filter((x: any) =>
            x.regions.includes(Number(regionStore?.region))
        ).map<any>((x: any) => ({
            ...x,
            name: t(x.name),
        }));
        const sortedList = list.sort((a: any, b: any) => a.name.localeCompare(b.name));

        return sortedList;
    };

    const getRuleShippingCompanyValuesByRegion = () => {
        const list = RuleShippingCompanyValues.filter((x: any) =>
            x.regions.includes(Number(regionStore?.region))
        ).map<any>((x: any) => ({
            ...x,
            name: t(x.name),
        }));
        const sortedList = list.sort((a: any, b: any) => a.name.localeCompare(b.name));

        return sortedList;
    };

    const getRulePaymentMethodValuesByRegion = () => {
        const list = RulePaymentMethodValues.filter((x: any) =>
            x.regions.includes(Number(regionStore?.region))
        ).map<any>((x: any) => ({
            ...x,
            name: t(x.name),
        }));
        const sortedList = list.sort((a: any, b: any) => a.name.localeCompare(b.name));

        return sortedList;
    };

    const getRulePaymentCollectorValuesByRegion = () => {
        const list = RulePaymentCollectorValues.filter((x: any) =>
            x.regions.includes(Number(regionStore?.region))
        ).map<any>((x: any) => ({
            ...x,
            name: t(x.name),
        }));
        const sortedList = list.sort((a: any, b: any) => a.name.localeCompare(b.name));
        return sortedList;
    };

    const getRuleCreditCardTypeValuesByRegion = () => {
        const list = RuleCreditCardTypeValues.filter((x: any) =>
            x.regions.includes(Number(regionStore?.region))
        ).map<any>((x: any) => ({
            ...x,
            name: t(x.name),
        }));
        const sortedList = list.sort((a: any, b: any) => a.name.localeCompare(b.name));

        return sortedList;
    };

    const getRulePlatformValuesByRegion = () => {
        const list = RulePlatformValues.filter((x: any) => x.regions.includes(Number(regionStore?.region))).map<any>(
            (x: any) => ({
                ...x,
                name: t(x.name),
            })
        );
        const sortedList = list.sort((a: any, b: any) => a.name.localeCompare(b.name));

        return sortedList;
    };

    const getCouponCharacterSetTypesByRegion = () => {
        const list = CouponCharacterSetTypes.filter((x: any) =>
            x.regions.includes(Number(regionStore?.region))
        ).map<any>((x: any) => ({
            ...x,
            name: t(x.name),
        }));
        const sortedList = list.sort((a: any, b: any) => a.name.localeCompare(b.name));

        return sortedList;
    };

    const filterLiveCampaign = (status: number) => {
        setLiveCampaignStatus(status);
    };

    useEffect(() => {
        const campaignId: number = parseInt(id!);
        if (campaignId > 0) {
            getCampaign(campaignId);
        }

        if (properties.campaignId > 0) {
            setExpanded({
                campaingMainProperties: true,
                couponUsage: false,
                thirdPartyCampaigns: false,
                publicationInformation: false,
                freeCargo: false,
                campaignDuration: false,
                backOfficeUser: false,
            });
        }
    }, []);

    useEffect(() => {
        getAssigneeList(Number(regionStore?.region));
    }, [countryId]);

    useEffect(() => {
        if (campaignDetail != undefined && campaignDetail.campaignRule != undefined) {
            updateProperties();
        }
    }, [campaignDetail.campaignRule]);

    const showLoading = () => {
        loadingProgressCount += 1;
        setLoading(true);
    };

    const hideLoading = () => {
        loadingProgressCount -= 1;
        if (loadingProgressCount == 0) {
            setLoading(false);
        }
    };

    const advanceToNextStep = () => {
        if (step < CampaignSteps[CampaignSteps.length - 1].id) {
            const newStep = step + 1;
            if (newStep < CampaignSteps.length) {
                setStep(newStep);
            }
        }
    };

    const stepperOnClickNext = async (event: any) => {
        event.preventDefault();

        const saveResult = await saveCampaignForm({});
        if (saveResult) {
            advanceToNextStep();
        }
    };

    const stepperOnClickReset = () => {
        if (step >= CampaignSteps[0].id) {
            if (step > 0) {
                setStep(step - 1);
                setCampaignDetail(campaignDetailInitial);
            }
        }
    };

    const stepperOnClick = (stepId: number) => {
        if (campaignDetail == undefined) return;
        if (campaignDetail.campaignRule == undefined) return;

        const condition1 = stepId < step;
        const condition2 = campaignDetail.campaignRule.status != CampaignStatus.Template;

        if (condition1 || condition2) {
            setStep(stepId);
            return;
        }
    };

    const handleExpandClick = (name: string, value: boolean) => {
        setExpanded({ ...expanded, [name]: !value });
    };

    const getAssigneeList = async (id?: any) => {
        const response = await CampaignManagementQuery.GetAssigneeList({
            regionId: Number(id),
        });

        let data: AssigneeOptionType[] = [];
        if (response && response.data?.length > 0) {
            data = response.data.map((item: any) => ({
                id: item.assignee,
                name: item.assignee,
            }));
        }

        setAssignees(data);
    };

    const getCampaign = async (campaignId: number) => {
        showLoading();
        const result = await CampaignManagementQuery.GetCampaign({
            Id: campaignId,
        });

        const selectedCountry = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId;

        dispatch({
            type: StoreActionType.SET_LAST_REGION,
            payload: {
                setLastRegion: Number(selectedCountry),
            },
        });
        history.push(`/campaign/campaignmanagement/save/${campaignId}`);

        if (result?.isSuccess && result.data != undefined) {
            setCampaignDetailInitial(JSON.parse(JSON.stringify(result.data)));
            setCampaignDetail(result.data);
        } else {
            dispatch(
                showSnackbar({
                    message: t('campaignManagement.save.step.message.notFound'),
                    status: enumSnackbarStatusType.warning,
                })
            );
        }

        hideLoading();

        return result;
    };

    const updateProperties = () => {
        let bDate = campaignDetail.campaignRule.beginDate?.split('T')[0] ?? '';
        let eDate = campaignDetail.campaignRule.endDate?.split('T')[0] ?? '';

        let startTime = campaignDetail.campaignRule.beginDate?.split('T')[1]?.substring(0, 5) ?? '';
        let endTime = campaignDetail.campaignRule.endDate?.split('T')[1]?.substring(0, 5) ?? '';

        setProperties({
            ...properties,
            assignee: campaignDetail.campaignRule.assignee,
            beginDate: bDate,
            campaignId: campaignDetail.campaignRule.id,
            campaignName: campaignDetail.campaignRule.title,
            endDate: eDate,
            endTime: endTime,
            priority: campaignDetail.campaignRule.processOrder,
            reiteration: campaignDetail.campaignRule.repeatType,
            recurrences: campaignDetail.repeateDays,
            startTime: startTime,
            tag: campaignDetail.campaignRule.tag ?? '',
            status: campaignDetail.campaignRule.status,
        });
    };

    const getBase64 = (file: any) => {
        const reader = new FileReader();
        return new Promise((resolve) => {
            reader.addEventListener('load', () => resolve(String(reader.result)));
            reader.readAsDataURL(file);
        });
    };

    const getBase64StringFromDataURL = (dataURL: any) => dataURL.split(',')[1];

    const downloadFile = async (data: { fileName?: string; documentData?: string }) => {
        try {
            const fileContent = data?.documentData;
            if (!fileContent) {
                dispatch(
                    showSnackbar({
                        message: t('campaignManagement.save.step.message.downloadFileNotFound'),
                        status: enumSnackbarStatusType.danger,
                    })
                );

                return;
            }

            const fileName = data.fileName || 'download.xlsx';
            const linkSource = `data:application/vnd.ms-excel;base64,${fileContent}`;

            const downloadLink = document.createElement('a');
            downloadLink.href = linkSource;
            downloadLink.download = fileName;

            downloadLink.click();

            // Tarayıcı belleğinden bağlantıyı temizler.
            window.URL.revokeObjectURL(downloadLink.href);
        } catch (error) {
            dispatch(
                showSnackbar({
                    message: `${t('campaignManagement.save.step.message.savingFileAnErrorOccurred')} ${error}`,
                    status: enumSnackbarStatusType.danger,
                })
            );
        }
    };

    const validateDateAndTime = (errors: any) => {
        const today = new Date();

        const beginDateObj = new Date(properties.beginDate);
        const endDateObj = new Date(properties.endDate);

        //begin date

        if (properties.beginDate === '') {
            errors.beginDate = t('campaignManagement.save.step.properties.beginDate.validationMessage.required');
        }

        if (properties.beginDate.length > 10) {
            errors.beginDate = t('campaignManagement.save.step.properties.beginDate.validationMessage.maxLength');
        }

        if (beginDateObj?.toISOString()?.split('T')?.[0] === today?.toISOString()?.split('T')?.[0]) {
            delete errors.beginDate;
        }
        // } else {
        //     if (beginDateObj < today) {
        //         errors.beginDate = t('campaignManagement.save.step.properties.beginDate.validationMessage.beforeToday');
        //     }
        // }

        //end date

        if (properties.endDate.length > 10) {
            errors.endDate = t('campaignManagement.save.step.properties.endDate.validationMessage.maxLength');
        }

        // if (endDateObj <= beginDateObj) {
        //     errors.endDate = t('campaignManagement.save.step.properties.endDate.validationMessage.beforeBegin');
        // }

        if (endDateObj?.toISOString()?.split('T')?.[0] === today?.toISOString()?.split('T')?.[0]) {
            delete errors.endDate;
        } else {
            // if (endDateObj < today) {
            //     errors.endDate = t('campaignManagement.save.step.properties.endDate.validationMessage.beforeToday');
            // }
        }

        if (properties.endDate === '') {
            errors.endDate = t('campaignManagement.save.step.properties.endDate.validationMessage.required');
        }

        //start time
        if (properties.startTime === '') {
            errors.startTime = t('campaignManagement.save.step.properties.startTime.validationMessage.required');
        }

        //end time
        if (properties.endTime === '') {
            errors.endTime = t('campaignManagement.save.step.properties.endTime.validationMessage.required');
        }
    };

    const validateAssingee = (errors: any) => {
        //assignee
        if (properties.assignee == undefined || (properties.assignee as string).length == 0) {
            errors.assignee = t('campaignManagement.save.step.properties.assignee.validationMessage.required');
        } else {
            const isEmail =
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                    properties.assignee as string
                );
            if (!isEmail) {
                errors.assignee = t('campaignManagement.save.step.properties.assignee.validationMessage.enterAnEmail');
            }
        }
    };

    const onValidate = () => {
        const errors = {} as any;

        //campaign name
        if (properties.campaignName.length < 2) {
            errors.campaignName = t('campaignManagement.save.step.properties.campaignName.validationMessage.minLength');
        }
        if (properties.campaignName.length > 50) {
            errors.campaignName = t('campaignManagement.save.step.properties.campaignName.validationMessage.maxLength');
        }

        //tag
        if (properties.tag != null && properties.tag.length > 0 && properties.tag.length < 2) {
            errors.tag = t('campaignManagement.save.step.properties.tag.validationMessage.minLength');
        }
        if (properties.tag != null && properties.tag.length > 0 && properties.tag.length > 30) {
            errors.tag = t('campaignManagement.save.step.properties.tag.validationMessage.maxLength');
        }
        if (/\s+/.test(properties.tag)) {
            errors.tag = t('campaignManagement.save.step.properties.tag.validationMessage.spaceless');
        }

        validateDateAndTime(errors);

        //recurrences
        if (properties.reiteration == CampaignRepeatType.Weekly && properties.recurrences?.length == 0) {
            errors.recurrences = t('campaignManagement.save.step.properties.recurrence.validationMessage.required');
        }

        validateAssingee(errors);

        return errors;
    };

    const saveCampaignForm = async (params?: any) => {
        let validateResult = onValidate();
        setErrors(validateResult);

        if (Object.keys(validateResult).length > 0) return;

        showLoading();

        let result: boolean = false;

        let beginDate: string = `${properties.beginDate}T${properties.startTime}`;
        let endDate: string = `${properties.endDate}T${properties.endTime}`;

        const payload = {
            ...campaignDetail,
            campaignRule: {
                ...campaignDetail.campaignRule,
                id: properties.campaignId,
                beginDate: beginDate,
                endDate: endDate,
                title: properties.campaignName,
                processOrder: properties.priority ?? 0,
                assignee: properties.assignee,
                tag: properties.tag ?? '',
                repeatType: properties.reiteration,
                isCouponCampaign:
                    params?.isCouponCampaign !== undefined
                        ? params.isCouponCampaign
                        : !!campaignDetail?.campaignRule?.isCouponCampaign,
            },
            region: properties.region,
            culture: Helper.getLang(),
            repeateDays: properties.recurrences,
            updateCachedVersion: updatedCachedVersion,
            badges: params?.badges ?? campaignDetail?.badges,
            descriptionTexts: params?.descriptionTexts ?? campaignDetail?.descriptionTexts,
        };

        const saveResult = await CampaignManagementCommand.SaveCampaign(payload);
        if (saveResult?.isSuccess && saveResult.data?.id > 0) {
            setUpdatedCacheVersion(initialUpdatedCachedVersion);

            let campaignId = saveResult.data.id as number;

            await getCampaign(campaignId);

            dispatch(
                showSnackbar({
                    message: t('campaignManagement.save.step.message.successRecord'),
                    status: enumSnackbarStatusType.success,
                })
            );

            result = true;
        } else {
            dispatch(
                showSnackbar({
                    message: `${t('campaignManagement.save.step.message.anErrorOccurredWhileSaving')} ${
                        saveResult.message
                    }`,
                    status: enumSnackbarStatusType.danger,
                })
            );

            result = false;
        }

        hideLoading();

        return result;
    };

    return {
        id,
        initialState,
        expanded,
        setExpanded,
        errors,
        setErrors,
        loading,
        showLoading,
        hideLoading,
        step,
        setStep,
        liveCampaignStatus,
        campaignDetailInitial,
        setCampaignDetailInitial,
        campaignDetail,
        setCampaignDetail,
        properties,
        setProperties,
        assignees,
        getAssigneeList,
        updatedCachedVersion,
        setUpdatedCacheVersion,
        getRuleCustomerTypeValuesByRegion,
        getRuleDeliveryMethodValuesByRegion,
        getRuleShippingCompanyValuesByRegion,
        getRulePaymentMethodValuesByRegion,
        getRulePaymentCollectorValuesByRegion,
        getRuleCreditCardTypeValuesByRegion,
        getRulePlatformValuesByRegion,
        getCouponCharacterSetTypesByRegion,
        getBase64,
        getBase64StringFromDataURL,
        filterLiveCampaign,
        validateDateAndTime,
        handleExpandClick,
        stepperOnClickNext,
        advanceToNextStep,
        stepperOnClickReset,
        stepperOnClick,
        downloadFile,
        saveCampaignForm,
    };
};

export const StepWebsiteAppearenceServiceComponent = (props: any) => {
    const dispatch = useDispatch();

    const { t } = useTranslation();

    const regionStore = useSelector((state: any) => state.region);

    const campaignService = props.campaignService;
    const { showLoading, hideLoading, campaignDetail, updatedCachedVersion, setUpdatedCacheVersion } = campaignService;

    const [errors, setErrors] = useState({});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const countryId: any = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId?.toString();
    const languages = JSON.parse(import.meta.env.VITE_LANGUAGE_LIST!).filter((x: any) => {
        return x.countries.includes(countryId?.toString());
    });

    const [campaignMessages, setCampaignMessages] = useState<ICampaignDictionaryItem[]>([]);
    const [messageChanged, setMessageChanged] = useState(false);

    const [badgeButtons, setBadgeButtons] = useState<BadgeButtonProps[]>();
    const [badges, setBadges] = useState<IBadge>();
    const [selectedBadgeButton, setSelectedBadgeButton] = useState<SelectedBadgeButtonProps>();

    const [searchTerm, setSearchTerm] = useState<string>();
    const [searchResults, setSearchResults] = useState<ISearchResult>({
        folders: [],
        assets: [],
    });

    const [selectedTreeViewItem, setSelectedTreeViewItem] = useState<any>();
    const [nestedFolders, setNestedFolders] = useState<any>([]);

    const [selectAll, setSelectAll] = useState(false);
    const [selectedImages, setSelectedImages] = useState<any[]>([]);

    const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
    const [fileTable, setFileTable] = useState<any[]>([]);

    const [newFolder, setNewFolder] = useState<any>({
        isRequest: false,
        name: '',
    });

    const [imageOverlay, setImageOverlay] = useState({
        id: '',
        visible: false,
    });

    const [previewOverlay, setPreviewOverlay] = useState({
        visible: false,
        imageSource: '',
    });

    const searchTermRef = useRef<HTMLInputElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const assetTarget = {
        web: 'Web',
        mobileWeb: 'MobileWeb',
        mobileApp: 'MobileApp',
    };

    const getCampaignMessages = () => {
        const messages = campaignService.campaignDetail.descriptionTexts ?? [];
        setCampaignMessages(messages);
    };

    const getCampaignBadges = async () => {
        let data =
            languages &&
            languages?.map((lang: any) => {
                let badge = null;
                if (campaignService.campaignDetail?.badges.length > 0) {
                    badge = campaignService.campaignDetail?.badges.find((x: any) => x.culture == lang.short_code);
                }

                let web: string = badge != null ? (badge.web ?? '') : '';
                let mobileWeb: string = badge != null ? (badge.mobileWeb ?? '') : '';
                let mobileApp: string = badge != null ? (badge.mobileApp ?? '') : '';
                let badgeWebName: string = badge != null ? (badge.badgeWebName ?? '') : '';
                let badgeMobileWebName: string = badge != null ? (badge.badgeMobileWebName ?? '') : '';
                let badgeMobileAppName: string = badge != null ? (badge.badgeMobileAppName ?? '') : '';

                let badgeList: any = [];

                badgeList.push({
                    id: `${lang.short_code}_${assetTarget.web}`,
                    title: t('campaignManagement.save.step.websiteAppearance.badge.item.website'),
                    source: web,
                    assetTarget: assetTarget.web,
                    badgeName: badgeWebName,
                });

                badgeList.push({
                    id: `${lang.short_code}_${assetTarget.mobileWeb}`,
                    title: t('campaignManagement.save.step.websiteAppearance.badge.item.responsive'),
                    source: mobileWeb,
                    assetTarget: assetTarget.mobileWeb,
                    badgeName: badgeMobileWebName,
                });

                badgeList.push({
                    id: `${lang.short_code}_${assetTarget.mobileApp}`,
                    title: t('campaignManagement.save.step.websiteAppearance.badge.item.application'),
                    source: mobileApp,
                    assetTarget: assetTarget.mobileApp,
                    badgeName: badgeMobileAppName,
                });

                return {
                    culture: lang.short_code,
                    badges: badgeList,
                };
            });

        setBadgeButtons(data);
    };

    const getBadges = async () => {
        let result = await CampaignManagementQuery.GetBadges({
            regionId: Number(regionStore?.region),
        });

        setBadges(result.data);
    };

    const getNestedFolders = () => {
        let folderList: any = [];

        if (badges?.badgeAssetsFolders && badges?.badgeAssetsFolders.length > 0) {
            folderList = createTreeStructure(
                badges?.badgeAssetsFolders?.sort(function (a: any, b: any) {
                    return a.type - b.type;
                })
            );
        }

        setNestedFolders(folderList);
    };

    useEffect(() => {
        if (campaignService.campaignDetail == undefined) return;

        if (campaignService.campaignDetail.descriptionTexts != undefined) getCampaignMessages();
        if (campaignService.campaignDetail.badges != undefined) getCampaignBadges();
        // Only re-hydrate local UI state when the loaded campaign changes, not on every local edit to campaignDetail
    }, [campaignService.campaignDetail?.campaignRule?.id]);

    useEffect(() => {
        filterBadgeList(selectedTreeViewItem?.id);
        getNestedFolders();
    }, [badges]);

    useEffect(() => {
        setNewFolder({
            name: '',
            isRequest: false,
        });
    }, [searchResults]);

    useEffect(() => {
        if (selectedFiles.length > 0) {
            getTableList(selectedFiles);
        }
    }, [selectedFiles]);

    useEffect(() => {
        if (isModalOpen) {
            getBadges();
            getNestedFolders();
        }
    }, [isModalOpen && selectedBadgeButton]);

    useEffect(() => {
        if (!messageChanged) return;
        setMessageChanged(false);

        const nextErrors = { ...errors } as any;
        const validateResult = validateCampaignMessage(nextErrors, false);
        if (Object.keys(validateResult).length > 0) {
            setErrors(validateResult);
            return;
        }

        setCampaignDetailForCampaignMessage(campaignMessages);
    }, [messageChanged]);

    const toDescriptionTextsPayload = (messages: ICampaignDictionaryItem[]) =>
        (messages ?? []).map((m: any) => ({
            languageId: Number(m.languageId),
            keywordValue: m.keywordValue ?? '',
        }));

    const buildCampaignBadgesPayload = (badgeList: any[]) => {
        let list: any = [];
        if (badgeList.length > 0) {
            badgeList.map((b) => {
                const isSelectedItem = b.badges.some(
                    (x: any) => (x.source && x.source.length > 0) || (x.badgeName && x.badgeName.length > 0)
                );
                if (!isSelectedItem) return;

                let culture = b.culture;
                let web: string = '';
                let mobileWeb: string = '';
                let mobileApp: string = '';
                let badgeWebName: string = '';
                let badgeMobileWebName: string = '';
                let badgeMobileAppName: string = '';

                if (b.badges.length > 0) {
                    b.badges.map((x: any) => {
                        switch (x.assetTarget) {
                            case assetTarget.web:
                                web = x.source;
                                badgeWebName = x.badgeName ?? '';
                                break;
                            case assetTarget.mobileWeb:
                                mobileWeb = x.source;
                                badgeMobileWebName = x.badgeName ?? '';
                                break;
                            case assetTarget.mobileApp:
                                mobileApp = x.source;
                                badgeMobileAppName = x.badgeName ?? '';
                                break;
                        }
                    });
                }

                list.push({
                    culture: culture,
                    web: web,
                    mobileWeb: mobileWeb,
                    mobileApp: mobileApp,
                    badgeWebName: badgeWebName,
                    badgeMobileWebName: badgeMobileWebName,
                    badgeMobileAppName: badgeMobileAppName,
                });
            });
        }
        return list;
    };

    const setCampaignDetailForBadge = (badgeList: any[]) => {
        campaignService.setCampaignDetail({
            ...campaignService.campaignDetail,
            badges: buildCampaignBadgesPayload(badgeList),
            descriptionTexts: toDescriptionTextsPayload(campaignMessages),
        });
    };

    const getBadgeNameErrorKey = (culture: string, assetTarget: string) => `badgeName${culture}${assetTarget}`;

    const validateBadgeName = (errors: any, value: string, inputName: string) => {
        delete errors[inputName];

        if (value && value.length > 22) {
            errors[inputName] = t(
                'campaignManagement.save.step.websiteAppearance.badge.item.validationMessage.maxLength'
            );
        }
    };

    const validateBadgeNames = (errors: any) => {
        if (!badgeButtons) return errors;

        badgeButtons.forEach((badgeButton) => {
            badgeButton.badges?.forEach((badge) => {
                const inputName = getBadgeNameErrorKey(badgeButton.culture, badge.assetTarget);
                validateBadgeName(errors, badge.badgeName ?? '', inputName);
            });
        });

        return errors;
    };

    const handleChangeBadgeName = (culture: string, assetTarget: string, value: string) => {
        if (!badgeButtons) return;

        const badgeButtonList: BadgeButtonProps[] = badgeButtons.map((badgeButton) => {
            if (badgeButton.culture !== culture) return badgeButton;

            return {
                ...badgeButton,
                badges: badgeButton.badges.map((badge) =>
                    badge.assetTarget === assetTarget ? { ...badge, badgeName: value } : badge
                ),
            };
        });

        setBadgeButtons(badgeButtonList);

        const nextErrors = { ...errors } as any;
        validateBadgeName(nextErrors, value, getBadgeNameErrorKey(culture, assetTarget));
        setErrors(nextErrors);
    };

    const handleBlurBadgeName = (culture: string, assetTarget: string, value: string) => {
        if (!badgeButtons) return;

        const badgeButtonList: BadgeButtonProps[] = badgeButtons.map((badgeButton) => {
            if (badgeButton.culture !== culture) return badgeButton;

            return {
                ...badgeButton,
                badges: badgeButton.badges.map((badge) =>
                    badge.assetTarget === assetTarget ? { ...badge, badgeName: value } : badge
                ),
            };
        });

        setBadgeButtons(badgeButtonList);

        const nextErrors = { ...errors } as any;
        validateBadgeName(nextErrors, value, getBadgeNameErrorKey(culture, assetTarget));
        setErrors(nextErrors);

        if (nextErrors[getBadgeNameErrorKey(culture, assetTarget)]) return;

        setCampaignDetailForBadge(badgeButtonList);
    };

    const setCampaignDetailForCampaignMessage = (campaignMessageList: any[]) => {
        campaignService.setCampaignDetail({
            ...campaignService.campaignDetail,
            descriptionTexts: toDescriptionTextsPayload(campaignMessageList),
        });
    };

    const handleChangeCampaignMessage = (event: any) => {
        const { id, value } = event.target;
        const languageId = Number(id);

        let _campaignMessages: ICampaignDictionaryItem[] = [...campaignMessages];
        let _index: number = _campaignMessages.findIndex((o) => Number(o.languageId) == languageId);
        if (_index > -1) {
            _campaignMessages[_index] = {
                ..._campaignMessages[_index],
                languageId,
                keywordValue: value,
                changed: true,
            };
        } else {
            _campaignMessages.push({
                languageId,
                keywordValue: value,
                changed: true,
            } as ICampaignDictionaryItem);
        }

        setCampaignMessages(_campaignMessages);

        setUpdatedCacheVersion({
            ...updatedCachedVersion,
            keywords: [campaignDetail.campaignRule.descriptionKey, campaignDetail.campaignRule.offerKey],
        });
        setMessageChanged(true);
    };

    //badge button

    const deleteCampaignBadge = (culture: string, assetTarget: string) => {
        let badgeButtonList: BadgeButtonProps[] = [...badgeButtons!];

        let _index = badgeButtonList.findIndex((x) => x.culture == culture);
        if (_index > -1) {
            badgeButtonList[_index].badges.map((x) => {
                if (x.assetTarget == assetTarget) {
                    x.source = '';
                }
            });
        }

        setBadgeButtons(badgeButtonList);
        resetBadgeOverlay();
        setCampaignDetailForBadge(badgeButtonList);
    };

    //modal

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        resetModal();
    };

    const saveModal = () => {
        if (selectedImages.length == 0) {
            dispatch(
                showSnackbar({
                    message: t('campaignManagement.save.step.websiteAppearance.modal.message.selectImage'),
                    status: enumSnackbarStatusType.danger,
                })
            );
        } else if (selectedImages.length > 1) {
            dispatch(
                showSnackbar({
                    message: t('campaignManagement.save.step.websiteAppearance.modal.message.selectOnlyOneImage'),
                    status: enumSnackbarStatusType.danger,
                })
            );
        } else {
            setButtonSelectionImage(selectedImages);
            closeModal();
        }
    };

    const resetModal = () => {
        setBadges({
            badgeAssets: [],
            badgeAssetsFolders: [],
        } as IBadge);

        setSelectedTreeViewItem({});

        handleCloseNewFolder();

        setNewFolder({
            isRequest: false,
            name: '',
        });

        setSelectedImages([]);

        setSelectedBadgeButton({
            assetTarget: '',
            culture: '',
        });

        setImageOverlay({
            id: '',
            visible: false,
        });

        setPreviewOverlay({
            imageSource: '',
            visible: false,
        });
    };

    //filter data
    const filterBadgeList = (folderId?: any) => {
        folderId = folderId == undefined ? null : folderId;

        let filteredFolders: IAssetsFolder[] = [];
        let filteredAssets: IAsset[] = [];

        filteredFolders = badges?.badgeAssetsFolders.filter((f: any) => f.parentId == folderId) as IAssetsFolder[];
        filteredAssets = badges?.badgeAssets.filter((a: any) => a.folderId == folderId) as IAsset[];

        setSearchResults({
            folders: filteredFolders,
            assets: filteredAssets,
        });
    };

    //treeview
    const createTreeStructure = (flatData: any, parentId = null) => {
        const tree: any = [];
        const lookup: any = {};

        if (flatData) {
            flatData.forEach((node: any) => {
                const { id, parentId: nodeParentId } = node;
                lookup[id] = { ...node, children: lookup[id]?.children || [] };

                if (nodeParentId === parentId) {
                    tree.push(lookup[id]);
                } else {
                    lookup[nodeParentId] = lookup[nodeParentId] || {};
                    lookup[nodeParentId].children = lookup[nodeParentId]?.children || [];
                    lookup[nodeParentId].children.push(lookup[id]);
                }
            });
        }

        return tree;
    };

    const findItemAndParentsById = (data: NestedFolderProps[], targetId: string) => {
        let result: any = {};

        function findRecursive(item: NestedFolderProps, parents: NestedFolderProps[] = []) {
            if (item.id === targetId) {
                result = {
                    id: item.id,
                    name: item.name,
                    parents: parents.map((parent: any) => ({ id: parent.id, name: parent.name })).reverse(),
                };
                return;
            }

            if (item.children) {
                for (const child of item.children) {
                    findRecursive(child, [item, ...parents]);
                }
            }
        }

        for (const item of data) {
            findRecursive(item);
            if (Object.entries(result).length > 0) {
                break; // ID bulunduğunda döngüyü sonlandır
            }
        }

        if (result != null && result?.parents) {
            console.log(
                'SelectedNode',
                result.parents?.map((p: any) => p.id)
            );
        }

        return result;
    };

    const handleNodeSelect = (_: any, nodeId: string) => {
        let result = findItemAndParentsById(nestedFolders, nodeId);
        const isSameNode = selectedTreeViewItem?.id == nodeId;
        setSelectedTreeViewItem(result);

        filterBadgeList(nodeId);

        //dosya yükleme alanı kapatılır
        if (!isSameNode) {
            handleCloseNewFolder();
        }
    };

    //selection
    const toggleSelectAll = () => {
        setSelectAll(!selectAll);
        setSelectedImages(selectAll ? ([] as any) : searchResults?.assets?.map((image: any) => image.id));
    };

    const toggleImageSelection = (imageId: number) => {
        let _selectedImages = [...selectedImages];
        let index = _selectedImages.findIndex((x: any) => x == imageId);
        if (index > -1) {
            _selectedImages.splice(index, 1);
        } else {
            _selectedImages.push(imageId);
        }

        setSelectedImages(_selectedImages);
        return _selectedImages;
    };

    const setButtonSelectionImage = (images: any[]) => {
        let badgeButtonList: BadgeButtonProps[] = [...badgeButtons!];
        let isChanged = false;

        images.map((imageId: any) => {
            let badge = badges?.badgeAssets.find((x: any) => x.id == imageId);

            let _index = badgeButtonList.findIndex((x: any) => x.culture == selectedBadgeButton?.culture);
            if (_index > -1) {
                badgeButtonList[_index].badges.map((x: any) => {
                    if (x.assetTarget == selectedBadgeButton?.assetTarget) {
                        x.source = badge?.assetUrl ?? '';
                        isChanged = true;
                    }
                });
            }
        });

        if (isChanged) {
            setBadgeButtons(badgeButtonList);
            setCampaignDetailForBadge(badgeButtonList);
        }
    };

    const doubleClickImageSelection = (imageId: number) => {
        const imageList = toggleImageSelection(imageId);
        setButtonSelectionImage(imageList);
        closeModal();
    };

    //search
    const handleSearch = () => {
        const inputValue = searchTermRef.current?.value || '';
        setSearchTerm(inputValue);

        if (inputValue == undefined || inputValue.length == 0) {
            filterBadgeList(selectedTreeViewItem?.id);
        } else {
            const filteredFolders = badges?.badgeAssetsFolders.filter((folder: any) =>
                folder.name.toLowerCase().includes(inputValue.toLowerCase())
            ) as IAssetsFolder[];

            const filteredAssets = badges?.badgeAssets.filter((asset: any) =>
                asset.fileName.toLowerCase().includes(inputValue.toLowerCase())
            ) as IAsset[];

            setSearchResults({
                ...searchResults,
                folders: filteredFolders,
                assets: filteredAssets,
            });
        }
    };

    //new folder
    const handleRequestNewFolder = () => {
        setNewFolder({
            isRequest: true,
        });
    };

    const saveNewFolder = async (event: any) => {
        if (event.key === 'Enter') {
            const inputValue = event.target.value;

            if (inputValue.length > 0) {
                const folderObject = {
                    id: 0,
                    name: inputValue,
                    parentId: selectedTreeViewItem?.id,
                    type: WebsiteFolderType.UserFolder,
                    regionId: Number(regionStore?.region),
                } as IAssetsFolder;

                const response = await CampaignManagementCommand.CreateBadgeFolder(JSON.stringify(folderObject));

                if (response.isSuccess) {
                    let updatedFolders = [...(badges?.badgeAssetsFolders as IAssetsFolder[])];
                    updatedFolders.push(response.data as IAssetsFolder);

                    setBadges({
                        ...(badges as IBadge),
                        badgeAssetsFolders: updatedFolders,
                    });
                }

                setNewFolder({
                    ...newFolder,
                    name: '',
                    isRequest: false,
                });
            }
        }
    };

    //image uploads

    const getListFiles = async (currentFiles: any, files: any) => {
        const promiseFiles: any[] = [];
        for (let i = 0; i < files.length; i += 1) {
            promiseFiles.push(campaignService.getBase64(files[i]));
        }

        const fileListBase64 = await Promise.all(promiseFiles);
        const fileList = fileListBase64.map((base64, index) => ({
            name: files[index]?.name?.replace(/\s/g, '_'),
            base64: campaignService.getBase64StringFromDataURL(base64),
            type: files[index]?.type,
            status: 0, //todo : enum olacak 0: durumsuz, 1: pasif, 2: aktif
        }));

        let allFiles = [...currentFiles, ...fileList];
        allFiles = allFiles.map((f, index) => {
            return {
                ...f,
                index: index + 1,
            };
        });

        return allFiles;
    };

    const fileUpload = async (files: any[]) => {
        const newFiles = files.filter((x) => x.status == 0);
        const addedImages = newFiles.map((image: any) => ({
            index: image.index,
            file: image.base64,
            fileName: image.name,
            regionId: Number(regionStore?.region),
            folderId: selectedTreeViewItem?.id,
        }));

        const payload = {
            addedImages,
        };

        try {
            showLoading();

            const response = await CampaignManagementCommand.InsertImageMultiple(JSON.stringify(payload));

            if (response.isSuccess) {
                let fileList = files;

                fileList =
                    fileList &&
                    fileList.map((f: any) => {
                        let fileResponse = response.data.find((x: any) => x.index == f.index);
                        let status = f.status;
                        let message = f.message;

                        if (fileResponse != undefined) {
                            status = fileResponse.isSuccess ? 2 : 1; //todo : enum olacak 0: durumsuz, 1: pasif, 2: aktif
                            message = fileResponse.message;
                        }

                        return {
                            ...f,
                            status: status,
                            message: message,
                        };
                    });

                setSelectedFiles(fileList);
                console.log('File(s) uploaded successfully.');
            } else {
                console.error('File(s) upload failed.');
            }
        } catch (error) {
            console.error('Error during file upload:', error);
        }

        hideLoading();
    };

    const getTableList = (files: any) => {
        // Dosyaları tabloda göstermek için formatla
        const tableData = Array.from(files).map((file: any) => {
            return {
                id: file.index,
                name: file.name,
                base64: file.base64,
                type: file.type,
                status: file.status,
                message: file.message,
            };
        });

        setFileTable(tableData);
    };

    const handleFileChange = async (event: any) => {
        const files = await getListFiles(selectedFiles, event.target.files);
        setSelectedFiles(files);
    };

    const handleUpload = async () => {
        await fileUpload(selectedFiles).then(() => {
            getBadges();
        });
    };

    const handleAddImageClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleCloseNewFolder = () => {
        setSelectedFiles([]);
        setFileTable([]);
    };

    //overlay

    const handleImageMouseOver = (event: any, id: string) => {
        let source = event.currentTarget.src;

        if (!imageOverlay.visible) {
            setImageOverlay({
                id: id,
                visible: true,
            });
        }

        setPreviewOverlay({
            visible: true,
            imageSource: source,
        });
    };

    const handleImageMouseLeave = (event: any, id: string) => {
        if (imageOverlay.visible) {
            setImageOverlay({
                id: id,
                visible: false,
            });
        }

        setPreviewOverlay({
            visible: false,
            imageSource: '',
        });
    };

    const resetBadgeOverlay = () => {
        setImageOverlay({
            id: '',
            visible: false,
        });
        setPreviewOverlay({
            imageSource: '',
            visible: false,
        });
    };

    //form

    const validateMessage = (errors: any, message: any, inputName: string) => {
        delete errors[inputName];

        if (!message || message.keywordValue.length < 2) {
            errors[inputName] = t(
                'campaignManagement.save.step.websiteAppearance.form.campaignMessage.validationMessage.minLength'
            ).replace('{{minLength}}', '2');
            return;
        }
        if (message.keywordValue.length > 50) {
            errors[inputName] = t(
                'campaignManagement.save.step.websiteAppearance.form.campaignMessage.validationMessage.maxLength'
            ).replace('{{maxLength}}', '50');
        }
    };

    const validateCampaignMessage = (errors: any, checkAllInput: boolean = true) => {
        const filteredMessages = campaignMessages.filter((y: any) => (checkAllInput ? true : y.changed));

        if (languages.length > 0 && filteredMessages.length == 0) {
            languages.forEach((lang: any) => {
                const inputName = `campaignMessage${lang.name}`;
                const filteredMessage = campaignMessages.filter((y: any) =>
                    checkAllInput ? true : y.changed && y.languageId == lang.id
                );
                const message = filteredMessage[0] || null;
                validateMessage(errors, message, inputName);
            });
        } else {
            filteredMessages.forEach((message: any) => {
                const langObj = languages.find((x: any) => x.id == message.languageId);
                const inputName = `campaignMessage${langObj?.name}`;
                validateMessage(errors, message, inputName);
            });
        }
        return errors;
    };

    const onValidate = () => {
        const errors = {} as any;

        validateCampaignMessage(errors);
        validateBadgeNames(errors);

        return errors;
    };

    const saveWebsiteAppearanceCampaign = async () => {
        const validateResult = onValidate();
        setErrors(validateResult);

        if (Object.keys(validateResult).length > 0) return false;

        const descriptionTexts = toDescriptionTextsPayload(campaignMessages);
        const badges = buildCampaignBadgesPayload(badgeButtons ?? []);

        campaignService.setCampaignDetail({
            ...campaignService.campaignDetail,
            descriptionTexts,
            badges,
        });

        return campaignService.saveCampaignForm({
            descriptionTexts,
            badges,
        });
    };

    const saveForm = async (event: any) => {
        event.preventDefault();

        const saveResult = await saveWebsiteAppearanceCampaign();
        if (saveResult) {
            campaignService.advanceToNextStep();
        }
    };

    return {
        ...campaignService,
        errors,
        setErrors,
        isModalOpen,
        setIsModalOpen,
        languages,
        campaignMessages,
        nestedFolders,
        badgeButtons,
        selectedBadgeButton,
        setSelectedBadgeButton,
        searchTerm,
        setSearchTerm,
        searchResults,
        setSearchResults,
        selectedImages,
        setSelectedImages,
        selectAll,
        setSelectAll,
        selectedTreeViewItem,
        setSelectedTreeViewItem,
        newFolder,
        setNewFolder,
        fileTable,
        setFileTable,
        selectedFiles,
        setSelectedFiles,
        imageOverlay,
        previewOverlay,
        setPreviewOverlay,
        setCampaignDetailForBadge,
        setCampaignDetailForCampaignMessage,
        searchTermRef,
        fileInputRef,
        filterBadgeList,
        deleteCampaignBadge,
        openModal,
        closeModal,
        saveModal,
        toggleSelectAll,
        toggleImageSelection,
        doubleClickImageSelection,
        setButtonSelectionImage,
        handleChangeCampaignMessage,
        handleChangeBadgeName,
        handleBlurBadgeName,
        handleNodeSelect,
        handleSearch,
        handleRequestNewFolder,
        saveNewFolder,
        handleAddImageClick,
        handleCloseNewFolder,
        handleFileChange,
        handleUpload,
        handleImageMouseOver,
        handleImageMouseLeave,
        onValidate,
        saveWebsiteAppearanceCampaign,
        saveForm,
    };
};
